import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Fade,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    Alert,
    Zoom
} from '@mui/material';
import {
    Add as AddIcon,
    Class as ClassIcon,
    Delete as DeleteIcon,
    School as SchoolIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    ErrorOutline as ErrorIcon,
    CheckCircleOutline as SuccessIcon
} from '@mui/icons-material';
import {AnimatePresence, motion} from 'framer-motion';
import {
    addClass,
    addSection,
    fetchClasses,
    removeClass,
    removeSection,
    submitClasses,
    updateClass
} from './redux/Action';
import {selectSchoolDetails} from '../../../../common';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ClassSection.css';

const ClassSection = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const {classes, loading, error} = useSelector((state) => state.classes);
    const userData = useSelector(selectSchoolDetails);
    const schoolId = userData?.id;
    const session = userData?.session;

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isSection, setIsSection] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expandedCards, setExpandedCards] = useState({});

    useEffect(() => {
        if (schoolId && session) {
            dispatch(fetchClasses(schoolId, session));
        }
    }, [dispatch, schoolId, session]);

    const handleClassChange = (e, index) => {
        const updatedClasses = classes.map((c, i) =>
            i === index ? {...c, name: e.target.value, schoolId, session} : c
        );
        dispatch(updateClass({index, updatedClass: updatedClasses[index]}));
    };

    const handleSectionChange = (e, classIndex, sectionIndex) => {
        const updatedClasses = classes.map((c, i) =>
            i === classIndex
                ? {
                    ...c,
                    sections: c.sections.map((s, j) =>
                        j === sectionIndex ? {...s, name: e.target.value, schoolId, session} : s
                    ),
                }
                : c
        );
        dispatch(updateClass({index: classIndex, updatedClass: updatedClasses[classIndex]}));
    };

    const isValidName = (name) => {
        if (!name || name.trim() === '') return false;
        return /^[a-zA-Z0-9 ]*$/.test(name);
    };

    const isFormValid = () => {
        if (!classes || classes.length === 0) return false;
        return classes.every(
            (schoolClass) =>
                isValidName(schoolClass.name) &&
                schoolClass.sections &&
                // Allow classes without sections, but if sections exist, they must have valid names
                (schoolClass.sections.length === 0 ||
                    schoolClass.sections.every((section) => isValidName(section.name)))
        );
    };

    const handleSaveChanges = async () => {
        if (!isFormValid()) {
            toast.error('Please fill all class and section names with valid values');
            return;
        }
        setSaving(true);
        try {
            await dispatch(submitClasses(classes, schoolId, session));
        } catch (error) {
            console.error('Error saving classes:', error);
        } finally {
            setSaving(false);
        }
    };

    const toggleCard = (index) => {
        setExpandedCards(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleOpenModal = (id, isSection = false, classIndex = null, sectionIndex = null) => {
        setSelectedItem({id, classIndex, sectionIndex});
        setIsSection(isSection);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedItem(null);
        setIsSection(false);
    };

    const handleDelete = () => {
        if (selectedItem) {
            if (isSection) {
                dispatch(removeSection(selectedItem.classIndex, selectedItem.sectionIndex, selectedItem.id));
            } else {
                dispatch(removeClass(selectedItem.id));
            }
            handleCloseModal();
        }
    };

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{padding: 3}}>
                    {/* Header Skeleton */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.dark, 0.3)} 100%)`,
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Skeleton variant="circular" width={40} height={40} />
                            <Skeleton variant="text" width={300} height={40} />
                        </Stack>
                    </Paper>

                    {/* Class Cards Skeleton */}
                    {[1, 2, 3].map((_, idx) => (
                        <Fade key={idx} in timeout={300 * (idx + 1)}>
                            <Card
                                sx={{
                                    marginBottom: 3,
                                    borderRadius: 2,
                                    boxShadow: theme.shadows[2],
                                }}
                            >
                                <CardContent>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={5}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Skeleton variant="circular" width={24} height={24} />
                                                <Skeleton variant="rectangular" height={56} sx={{ flex: 1, borderRadius: 1 }} />
                                                <Skeleton variant="circular" width={40} height={40} />
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={7}>
                                            <Stack spacing={2}>
                                                {[1, 2].map((_, secIdx) => (
                                                    <Stack key={secIdx} direction="row" spacing={2}>
                                                        <Skeleton variant="rectangular" height={56} sx={{ flex: 1, borderRadius: 1 }} />
                                                        <Skeleton variant="circular" width={40} height={40} />
                                                    </Stack>
                                                ))}
                                                <Skeleton variant="rectangular" height={36} width={150} sx={{ borderRadius: 1, mt: 2 }} />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Fade>
                    ))}

                    {/* Action Buttons Skeleton */}
                    <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                        <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1 }} />
                        <Skeleton variant="rectangular" width={160} height={40} sx={{ borderRadius: 1 }} />
                    </Stack>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box
                component={motion.div}
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                sx={{
                    padding: 3,
                    minHeight: '100vh',
                }}
            >
                {/* Header Section with Gradient */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 4,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        borderRadius: 3,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.35)}`,
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Box
                                sx={{
                                    bgcolor: alpha('#fff', 0.2),
                                    borderRadius: 2,
                                    p: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <SchoolIcon sx={{ fontSize: 40 }} />
                            </Box>
                            <Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 'bold',
                                        letterSpacing: '-0.5px',
                                    }}
                                >
                                    Class & Section Management
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        opacity: 0.9,
                                        mt: 0.5,
                                    }}
                                >
                                    Organize and manage your school classes and sections
                                </Typography>
                            </Box>
                        </Stack>
                        <Chip
                            icon={<SuccessIcon />}
                            label={`${classes.length} ${classes.length === 1 ? 'Class' : 'Classes'}`}
                            sx={{
                                bgcolor: alpha('#fff', 0.2),
                                color: 'white',
                                fontWeight: 'bold',
                                backdropFilter: 'blur(10px)',
                            }}
                        />
                    </Stack>
                </Paper>

                {/* Error Alert */}
                {error && (
                    <Zoom in>
                        <Alert
                            severity="error"
                            icon={<ErrorIcon />}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                boxShadow: theme.shadows[2],
                            }}
                            onClose={() => dispatch(fetchClasses(schoolId, session))}
                        >
                            {error}
                        </Alert>
                    </Zoom>
                )}

                {/* Empty State */}
                {!loading && classes.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Paper
                            sx={{
                                p: 6,
                                textAlign: 'center',
                                borderRadius: 3,
                                border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                            }}
                        >
                            <SchoolIcon
                                sx={{
                                    fontSize: 80,
                                    color: alpha(theme.palette.primary.main, 0.3),
                                    mb: 2,
                                }}
                            />
                            <Typography variant="h5" gutterBottom color="text.secondary">
                                No Classes Yet
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                Start by creating your first class to organize your school structure
                            </Typography>
                            <Button
                                onClick={() => dispatch(addClass({name: '', sections: [], schoolId}))}
                                variant="contained"
                                size="large"
                                startIcon={<AddIcon />}
                                sx={{
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    boxShadow: theme.shadows[4],
                                }}
                            >
                                Create First Class
                            </Button>
                        </Paper>
                    </motion.div>
                )}

                {/* Classes List */}

            <AnimatePresence>
                {classes.map((schoolClass, index) => (
                    <motion.div
                        key={index}
                        initial={{opacity: 0, x: -20}}
                        animate={{opacity: 1, x: 0}}
                        exit={{opacity: 0, x: 20}}
                        transition={{duration: 0.3}}
                    >
                        <Card
                            sx={{
                                marginBottom: 3,
                                borderRadius: 2,
                                boxShadow: theme.shadows[3],
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: theme.shadows[8],
                                },
                            }}
                        >
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={5}>
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                            <ClassIcon color="primary"/>
                                            <TextField
                                                label={`Class ${index + 1} Name`}
                                                fullWidth
                                                value={schoolClass.name}
                                                onChange={(e) => handleClassChange(e, index)}
                                                variant="outlined"
                                                error={!isValidName(schoolClass.name)}
                                                helperText={
                                                    !isValidName(schoolClass.name)
                                                        ? schoolClass.name.trim() === ''
                                                            ? 'Enter class name'
                                                            : 'Invalid class name'
                                                        : ''
                                                }
                                            />
                                            <Tooltip title="Delete Class" placement="top">
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleOpenModal(schoolClass.id)}
                                                    sx={{
                                                        '&:hover': {
                                                            transform: 'scale(1.1)',
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={7}>
                                        <AnimatePresence>
                                            {schoolClass.sections.map((section, secIndex) => (
                                                <motion.div
                                                    key={secIndex}
                                                    initial={{opacity: 0, height: 0}}
                                                    animate={{opacity: 1, height: 'auto'}}
                                                    exit={{opacity: 0, height: 0}}
                                                >
                                                    <Collapse in={true}>
                                                        <Grid container spacing={2} sx={{mb: 2}}>
                                                            <Grid item xs={10}>
                                                                <TextField
                                                                    label={`Section ${secIndex + 1}`}
                                                                    fullWidth
                                                                    value={section.name}
                                                                    onChange={(e) =>
                                                                        handleSectionChange(e, index, secIndex)
                                                                    }
                                                                    variant="outlined"
                                                                    error={!isValidName(section.name)}
                                                                    helperText={
                                                                        !isValidName(section.name)
                                                                            ? section.name.trim() === ''
                                                                                ? 'Enter section name'
                                                                                : 'Invalid section name'
                                                                            : ''
                                                                    }
                                                                />
                                                            </Grid>
                                                            <Grid item xs={2}>
                                                                <Tooltip title="Delete Section" placement="top">
                                                                    <IconButton
                                                                        color="error"
                                                                        onClick={() =>
                                                                            handleOpenModal(
                                                                                section.id,
                                                                                true,
                                                                                index,
                                                                                secIndex
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            '&:hover': {
                                                                                transform: 'scale(1.1)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <DeleteIcon/>
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Grid>
                                                        </Grid>
                                                    </Collapse>
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<AddIcon/>}
                                            onClick={() => dispatch(addSection(index))}
                                            sx={{
                                                mt: 2,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.02)',
                                                },
                                            }}
                                        >
                                            Add Section
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </motion.div>
                ))}
            </AnimatePresence>

            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 4,
                }}
            >
                <Button
                    onClick={() => dispatch(addClass({name: '', sections: [], schoolId}))}
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon/>}
                    sx={{
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'scale(1.05)',
                        },
                    }}
                >
                    Add Class
                </Button>
                <Button
                    onClick={handleSaveChanges}
                    variant="contained"
                    color="secondary"
                    disabled={!isFormValid() || saving}
                    startIcon={saving ? null : <SaveIcon />}
                    sx={{
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'scale(1.05)',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: alpha(theme.palette.secondary.main, 0.5),
                        },
                    }}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>

                <Dialog
                    open={modalOpen}
                    onClose={handleCloseModal}
                    PaperProps={{
                        component: motion.div,
                        initial: {opacity: 0, y: 20},
                        animate: {opacity: 1, y: 0},
                    }}
                >
                    <DialogTitle>
                        {isSection ? 'Delete Section?' : 'Delete Class?'}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this {isSection ? 'section' : 'class'}?
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleCloseModal}
                            color="primary"
                            variant="outlined"
                            sx={{mr: 1}}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            color="error"
                            variant="contained"
                            startIcon={<DeleteIcon/>}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default ClassSection;
